import React, { useEffect, useMemo, useRef, useState } from "react";
import ViewMoreBtn from "../../components/ViewMoreBtn";
import DataTable from "react-data-table-component";
import { LuFilter } from "react-icons/lu";
import { Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { AiOutlineDelete } from "react-icons/ai";
import { BsBellFill } from "react-icons/bs";
import io from "socket.io-client";
import DeleteModal from "../../components/DeleteModal";
import { Modal, Button, Form } from "react-bootstrap";
import {
  getAvailabilityTime,
  manageTimeAvailability,
} from "../../../../redux/slices/chatSlice";

const Chats = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [data, setData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [category, setCategory] = useState("");
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
  const { admin } = useSelector((state) => state.userSlice);
  const { refetch } = useSelector((state) => state.chatSlice);
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
  const [startHour, setStartHour] = useState("");
  const [startMinute, setStartMinute] = useState("");
  const [startAmPm, setStartAmPm] = useState("");

  const [endHour, setEndHour] = useState("");
  const [endMinute, setEndMinute] = useState("");
  const [endAmPm, setEndAmPm] = useState("");

  const socketRef = useRef();

  const prevProps = useRef({ refetch }).current;

  const { availability } = useSelector((state) => state.chatSlice);


  useEffect(() => {
    if (prevProps.refetch !== refetch && socketRef.current?.connected) {
      loadChats();
      dispatch(getAvailabilityTime());
    }
    return () => {
      prevProps.refetch = refetch;
    };
  }, [refetch]);

  // connect with the sockets
  useEffect(() => {
    if (Object.keys(admin).length === 0) return;

    console.log("Initializing Socket.IO connection...");

    // Initialize Socket.IO connection
    const socket = io(process.env.REACT_APP_SOCKET_URL, {
      extraHeaders: {
        token: localStorage.getItem("sgtoken"),
      },
    });

    // Set loading only on first load
    const isFirstLoad = data.length === 0;
    if (isFirstLoad) {
      setIsLoading(true);
    }

    // Add event listeners
    const handleConnect = () => {
      console.log("Connected Successfully");
      socket.emit("adduser", { id: admin._id });
      socket.emit("joinchat", { id: admin._id });
      loadChats(socket);
    };

    const handleNewChat = () => {
      console.log("Received new chat");
      loadChats(socket);
    };

    socket.on("connect", handleConnect);
    socket.on("receivechat", handleNewChat);

    socketRef.current = socket;

    // Cleanup function
    return () => {
      console.log("Disconnecting now");
      socket.off("connect", handleConnect);
      socket.off("receivechat", handleNewChat);
      socket.disconnect();
    };
  }, [admin]);

  const loadChats = (socket = socketRef.current) => {
    if (!socket || !socket.connected) {
      console.log("Socket not connected, cannot load chats");
      setIsLoading(false);
      return;
    }

    // Only show loading if it's the first load
    const isFirstLoad = data.length === 0;
    if (isFirstLoad) {
      setIsLoading(true);
    }

    socket.emit("getChatList", { id: admin._id }, (response) => {
      console.log("Chat list response:", response);
      if (response && response.data) {
        setData(Array.isArray(response.data) ? response.data : []);
      } else {
        setData([]);
      }
      setIsLoading(false);
    });
  };

  useEffect(() => {
    dispatch(getAvailabilityTime());
  }, []);

  const columns = [
    {
      name: "Messaged At",
      selector: (row) => {
        const date = new Date(row.createdAt);
        const formattedDate = date.toLocaleDateString("en-US", {
          month: "2-digit",
          day: "2-digit",
          year: "2-digit",
        });
        const formattedTime = date.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
        return `${formattedDate.replace(/\//g, "-")} at ${formattedTime}`;
      },
      sortable: true,
    },
    {
      name: "Sender",
      selector: (row) => {
        console.log(row, "row")
        return (
          <div className="d-flex align-items-center">
            <span>
              {row?.sender?.firstName || row?.sender?.lastName
                ? `${row?.sender?.firstName ?? ""} ${row?.sender?.lastName ?? ""}`.trim()
                : row?.sender?.fullName}
            </span>
            {row.unreadCount > 0 && (
              <div className="unread-badge ms-2">{row.unreadCount}</div>
            )}
          </div>
        );
      },
      sortable: true,
    },
    {
      name: "Message",
      selector: (row) => row?.latestMessage?.message,
      sortable: true,
      wrap: true,
    },
    {
      name: "Actions",
      selector: (row) => (
        <div className="d-flex">
          <AiOutlineDelete
            style={{ minWidth: "max-content" }}
            onClick={() => {
              setDeleteId(row?._id);
              setDeleteModal(true);
            }}
            size={19}
          />
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          <ViewMoreBtn redirectUrl={`/chat/${row._id}`} />
        </div>
      ),
    },
  ];

  const filteredData = useMemo(() => {
    return data.filter((item) => {
      const fullName = `${item?.sender?.firstName || ""} ${item?.sender?.lastName || ""
        }`.toLowerCase();

      return (
        fullName.includes(searchText.toLowerCase()) &&
        (category ? item.status?.props?.children === category : true)
      );
    });
  }, [data, searchText, category]);

  const hours = Array.from({ length: 12 }, (_, i) =>
    String(i + 1).padStart(2, "0")
  ); // 01-12
  const minutes = Array.from({ length: 60 }, (_, i) =>
    String(i).padStart(2, "0")
  ); // 00-59
  const ampmOptions = ["AM", "PM"];

  const handleSearch = (value) => {
    setSearchText(value);
    setResetPaginationToggle(!resetPaginationToggle);
  };

  return (
    <>
      <style>
        {`
          .unread-badge {
            background-color: rgb(8, 91, 40);
            color: white;
            border-radius: 50%;
            min-width: 20px;
            height: 20px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 12px;
            font-weight: bold;
            padding: 0 4px;
          }
          
          .notification-icon {
            position: relative;
            display: inline-flex;
            align-items: center;
          }
          
          .notification-count {
            position: absolute;
            top: -8px;
            right: -8px;
            background-color: rgb(8, 91, 40);
            color: white;
            border-radius: 50%;
            min-width: 18px;
            height: 18px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 10px;
            font-weight: bold;
          }
          
          .loading-container {
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 300px;
            width: 100%;
          }
        `}
      </style>

      {isLoading ? (
        <div className="loading-container">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <div>
          <div className="page-header">
            <div className="d-flex align-items-center justify-content-between">
              <h3 className="page-title">Chats</h3>
            </div>
            <div className="d-flex justify-content-end mt-3">
              <Button
                variant="success"
                onClick={() => setShowAvailabilityModal(true)}
              >
                Manage Availability
              </Button>
            </div>
          </div>
          {availability && (
            <div className="alert alert-info mt-3">
              <strong>Note:</strong> 📢 You’ve set your available time from{" "}
              <strong>
                {availability.startTime} - {availability.endTime}
              </strong>
              . Users will see a message indicating that you're unavailable
              outside these hours.
            </div>
          )}
          <div className="row">
            <div className="col-lg-12 grid-margin stretch-card">
              <div className="card">
                <div className="card-body py-4">
                  <DataTable
                    columns={columns}
                    data={filteredData}
                    pagination
                    paginationPerPage={20}
                    paginationRowsPerPageOptions={[
                      10, 20, 30, 50, 100, 200, 500,
                    ]}
                    highlightOnHover
                    pointerOnHover
                    subHeader
                    subHeaderAlign="left"
                    subHeaderComponent={
                      <Row className="mb-3 w-100">
                        <Col
                          md={8}
                          className="d-flex align-items-start flex-column gap-2"
                        >
                          <span className="d-flex align-items-center gap-2 me-3 filterby_label">
                            <LuFilter />
                            Filter By :
                          </span>
                          <div className="table_filter">
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Search by name"
                              value={searchText}
                              onChange={(e) => handleSearch(e.target.value)}
                            />
                          </div>
                        </Col>
                      </Row>
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <DeleteModal
        forChat
        deleteId={deleteId}
        deleteModal={deleteModal}
        setDeleteModal={setDeleteModal}
      />
      <Modal
        show={showAvailabilityModal}
        onHide={() => setShowAvailabilityModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Set Your Availability</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Start Time</Form.Label>
              <div className="d-flex gap-2">
                <Form.Select
                  value={startHour}
                  onChange={(e) => setStartHour(e.target.value)}
                >
                  <option value="">HH</option>
                  {hours.map((h) => (
                    <option key={h}>{h}</option>
                  ))}
                </Form.Select>
                <Form.Select
                  value={startMinute}
                  onChange={(e) => setStartMinute(e.target.value)}
                >
                  <option value="">MM</option>
                  {minutes.map((m) => (
                    <option key={m}>{m}</option>
                  ))}
                </Form.Select>
                <Form.Select
                  value={startAmPm}
                  onChange={(e) => setStartAmPm(e.target.value)}
                >
                  <option value="">AM/PM</option>
                  {ampmOptions.map((ap) => (
                    <option key={ap}>{ap}</option>
                  ))}
                </Form.Select>
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>End Time</Form.Label>
              <div className="d-flex gap-2">
                <Form.Select
                  value={endHour}
                  onChange={(e) => setEndHour(e.target.value)}
                >
                  <option value="">HH</option>
                  {hours.map((h) => (
                    <option key={h}>{h}</option>
                  ))}
                </Form.Select>
                <Form.Select
                  value={endMinute}
                  onChange={(e) => setEndMinute(e.target.value)}
                >
                  <option value="">MM</option>
                  {minutes.map((m) => (
                    <option key={m}>{m}</option>
                  ))}
                </Form.Select>
                <Form.Select
                  value={endAmPm}
                  onChange={(e) => setEndAmPm(e.target.value)}
                >
                  <option value="">AM/PM</option>
                  {ampmOptions.map((ap) => (
                    <option key={ap}>{ap}</option>
                  ))}
                </Form.Select>
              </div>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowAvailabilityModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              if (
                startHour &&
                startMinute &&
                startAmPm &&
                endHour &&
                endMinute &&
                endAmPm
              ) {
                const startTime = `${startHour}:${startMinute} ${startAmPm}`;
                const endTime = `${endHour}:${endMinute} ${endAmPm}`;
                console.log(
                  "Selected Availability:",
                  `${startTime} - ${endTime}`
                );
                dispatch(
                  manageTimeAvailability(availability._id, {
                    startTime,
                    endTime,
                  })
                );
                setShowAvailabilityModal(false);
              } else {
                alert("Please select full start and end time.");
              }
            }}
          >
            Save Availability
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Chats;
